<template>
  <v-container fluid class="pa-0">
    <ExecutionResultsDialog title="Fetch import details" ref="importDetailDialog" />
    <PageHeader title="Imported Data" icon="storage" no-padding />
    <v-alert :value="!hasId" outline class="pa-2" type="warning">
      <div class="primary--text">Sorry, we couldn't find this import.
        <v-btn small :to="{ name: 'allimports' }">
          {{$t('Button.ViewAllImports')}}
        </v-btn>
      </div>
    </v-alert>
    <PageLoader v-if="!(loadingHasFinish && states.isLoaded)" class="my-5" resourceName="import data" />
    <v-alert :value="true" v-else-if="states.isError" class="my-0" type="error" outline>
      <span class="primary--text">
        Failed to load import details, please contact Hatfield for assistance.
      </span>
    </v-alert>
    <v-card class="elevation-1 mb-2" v-else-if="currentImport && hasId">
      <v-layout>
        <v-flex>
          <v-card-title class="pb-2" style="align-items: baseline;">
            <div>
              <div class="headline">
                {{ currentImport.CreatedAt | format }}
              </div>
              <div class="body-2 font-weight-light">
                Import Date
              </div>
            </div>

            <RollbackImportAction v-if="isSystemAdmin" :importId="importId" />
          </v-card-title>

          <ImportDocuments v-if="isTenantAdmin" :documents="documents" />
          <div v-else-if="!isTenantAdmin && documents != null && documents.length > 0">
            <div class="title font-weight-regular">Documents</div>
            <v-btn v-for="document in documents" :key="document" depressed small :href="document.FilePath" download class="mx-0">
              <v-icon class="mr-1">
                save_alt
              </v-icon>
              <span class="text-none">{{ document.FileName }}</span>
            </v-btn>
          </div>

        </v-flex>
        <v-flex style="text-align: right; flex-shrink: 0">
          <v-card-text class="pb-2">
            <div class="headline">{{ currentImport.CocCode }}</div>
            <div class="body-2 font-weight-light">COC Code</div>
          </v-card-text>
          <v-card-text>
            <div class="title font-weight-regular">Source File</div>
            <div class="body-2" v-if="currentImport.FileDetails">
              <v-btn v-if="currentImport.FileDetails.ImportType == 'A125'" depressed small download class="mx-0" @click.prevent="downloadFile(currentImport.FileDetails)">
                <v-icon class="mr-1">
                  save_alt
                </v-icon>
                <div style="text-transform: none;">{{ currentImport.FileDetails.FileName }}</div>
              </v-btn>
              <SecondaryBtn v-else-if="currentImport.FileDetails.ImportType == 'INGESTION'" icon="description" small class="mx-0" :to="{ name: 'dataset', params: { id: currentImport.FileDetails.FileGuid }}">
                <div style="text-transform: none;">{{ currentImport.FileDetails.FileName }}</div>
              </SecondaryBtn>
            </div>
            <AlertChip v-else small type="warning">
              Source file not found.
            </AlertChip>
          </v-card-text>
        </v-flex>
      </v-layout>

      <v-divider />
      <v-layout wrap>
        <v-flex md4 xs12>
          <v-card-text class="pb-0">
            <div>
              <v-switch color="success" hide-details v-model="showNewOnly" label="Show new data only"></v-switch>
            </div>
          </v-card-text>          
        </v-flex>        
      </v-layout>

      <v-layout wrap>
        <v-flex md4 xs12 class="divider-right" :class="{ 'pt-1 pb-3': stationsToShow.length === 0 && analytesToShow.length === 0}">
          <v-card-text class="pb-0">
            <div>
              <span class="title font-weight-regular">Stations</span>
              <template>
                ⋅
                <span class="font-weight-light">{{ stationsToShow.length }}
                  {{ pluralize('item', stationsToShow.length)}}</span>
              </template>
            </div>
          </v-card-text>
          <v-data-table v-if="stationsToShow.length > 0" class="table-default-bg" :class="{ 'five-items': pagination.stations.rowsPerPage === 5, 'ten-items': pagination.stations.rowsPerPage === 10, 'twenty-five-items': pagination.stations.rowsPerPage === 25 }" :pagination.sync="pagination.stations" :items="stationsToShow" :headers="stationHeaders">
            <template slot="items" slot-scope="props">
              <td>{{props.item.SiteId}}</td>
            </template>
          </v-data-table>
        </v-flex>
        <v-flex md4 xs12 class="divider-right" :class="{ 'pt-1 pb-3': stationsToShow.length === 0 && analytesToShow.length === 0}">
          <v-card-text class="pb-0">
            <div>
              <span class="title font-weight-regular">Analytes</span>
              <template>
                ⋅
                <span class="font-weight-light">{{ analytesToShow.length }}
                  {{ pluralize('item', analytesToShow.length)}}
                </span>
              </template>
            </div>
          </v-card-text>
          <v-data-table v-if="analytesToShow.length > 0" class="table-default-bg" :items="analytesToShow" :headers="analyteHeaders" :pagination.sync="pagination.analytes" :class="{ 'five-items': pagination.analytes.rowsPerPage == 5, 'ten-items': pagination.analytes.rowsPerPage === 10, 'twenty-five-items': pagination.analytes.rowsPerPage === 25 }">
            <template slot="items" slot-scope="props">
              <td>{{props.item.AnalyteName}}</td>
            </template>
          </v-data-table>
        </v-flex>
        <v-flex md4 xs12 :class="{ 'pt-1 pb-3': stationsToShow.length === 0 && analytesToShow.length === 0 && currentImport.MediaTypes.length === 0}">
          <v-card-text class="pb-0">
            <div>
              <span class="title font-weight-regular">MediaTypes</span>
              <template>
                ⋅
                <span class="font-weight-light">{{ currentImport.MediaTypes.length }}
                  {{ pluralize('item', currentImport.MediaTypes.length)}}
                </span>
              </template>
            </div>
          </v-card-text>
          <v-data-table v-if="currentImport.MediaTypes.length > 0" class="table-default-bg" :items="currentImport.MediaTypes" :headers="mediaTypeHeaders" :pagination.sync="pagination.mediaTypes" :class="{ 'five-items': pagination.mediaTypes.rowsPerPage === 5, 'ten-items': pagination.mediaTypes.rowsPerPage === 10, 'twenty-five-items': pagination.mediaTypes.rowsPerPage === 25 }">
            <template slot="items" slot-scope="props">
              <td>{{props.item.MediaTypeName}}</td>
            </template>
          </v-data-table>
        </v-flex>
      </v-layout>


      <v-divider />
      <v-card class="elevation-0 mb-2" v-if="currentImport.Data.length">
        <v-card-title class="pb-0 align-center">
          <span>
            <span class="title font-weight-regular">Data</span>
            <template>
              ⋅
              <span class="font-weight-light">{{ dataItems.length }}
                {{ pluralize('item', dataItems.length)}}
              </span>
            </template>
          </span>
          <v-switch color="primary" class="ml-3 my-0" hide-details v-model="showDetails.data" label="Show details" />
          <v-spacer />
          <v-text-field prepend-inner-icon="search" v-model="search.data" class="rounded font-weight-regular" hide-details clearable solo placeholder="Search data" @click:clear="search.data = ''" />

        </v-card-title>
        <v-data-table class="table-default-bg" :search="search.data" :items="dataItems" :headers="dataHeaders" :pagination.sync="pagination.data" :class="{ 'five-items': pagination.data.rowsPerPage === 5, 'ten-items': pagination.data.rowsPerPage === 10, 'twenty-five-items': pagination.data.rowsPerPage === 25 }">
          <template slot="items" slot-scope="props">
            <td class="no-wrap">{{props.item.SampleDateTime | formatDate}}</td>
            <td class="no-wrap">{{props.item.SiteId}}</td>
            <td class="no-wrap">{{props.item.DisplayName}}</td>
            <td class="no-wrap">{{props.item.AnalyteName}}</td>

            <template v-if="showDetails.data">
              <td class="text-xs-right no-wrap">{{props.item.ResultQualifier}}</td>
              <td class="text-xs-right no-wrap">{{props.item.Result}}</td>
              <td>{{props.item.Units}}</td>
            </template>
            <template v-else>
              <td class="text-xs-right d-flex no-wrap" style="align-content: right; align-items: center;">
                <div>{{props.item.ResultQualifier}}</div>
                <v-spacer />
                <div> {{props.item.Result}} {{props.item.Units}}</div>
              </td>
            </template>

            <td class="text-xs-right no-wrap" :class="{ 'error--text': props.item.isBelowDetectionLimit}">
              {{props.item.DetectionLimit}}</td>
            <td class="no-wrap">
              <v-icon small>{{ getMediaTypeIcon(props.item.MediaType) }}</v-icon>
              {{props.item.MediaType}}
            </td>
          </template>
        </v-data-table>
      </v-card>
    </v-card>
  </v-container>
</template>

<script>
  import moment from 'moment'
  import ImportDocuments from './ImportDocuments'
  import DownloadUtil from '@/components/mixins/DownloadUtil'
  import { download } from '@/helpers/download'
  import {
    prettyDateTimeFormat,
    prettyDateFormat,
  } from '@/components/mixins/MomentUtil'

  import {
    createNamespacedHelpers,
  } from 'vuex'

  import {
    pluralize,
  } from '@/helpers/strings'


  import {
    getMediaTypeIcon,
  } from '@/helpers/mediaTypes'

  import format from 'date-fns/format'
  const imports = createNamespacedHelpers('imports')
  const authenticate = createNamespacedHelpers('authenticate')
  const sampleactivity = createNamespacedHelpers('sampleactivity')
  const app = createNamespacedHelpers('app')

  export default {
    name: 'Import',
    components: {
      ImportDocuments,
    },
    filters: {
      format(date) {
        var momentObject = moment.utc(date).toDate()        
        return moment(momentObject).format(prettyDateTimeFormat)
      },
      formatDate(date) {
        return format(date, prettyDateFormat)
      },
    },
    mixins: [DownloadUtil],
    props: {
      importId: [Number, String],
    },
    data() {
      return {
        showNewOnly: true,
        currentImport: null,
        search: {
          data: '',
        },
        states: {
          isLoaded: false,
          isError: false,
        },
        showDetails: {
          data: false,
        },
        approved: false,
        stationHeaders: [{
          text: 'Station Name',
          value: 'SiteId',
          align: 'left',
        }],
        analyteHeaders: [{
          text: 'Analyte Name',
          value: 'AnalyteName',
          align: 'left',
        }],
        mediaTypeHeaders: [{
          text: 'MediaType Name',
          value: 'MediatypeName',
          align: 'left',
        }],
        hasId: true,
        pagination: {
          stations: {
            rowsPerPage: 5,
            sortBy: 'SampleDateTime',
          },
          analytes: {
            rowsPerPage: 5,
          },
          mediaTypes: {
            rowsPerPage: 5,
          },
          data: {
            rowsPerPage: 5,
          },
        },
        file: {},
      }
    },
    computed: {
      ...imports.mapGetters(['importlist']),
      ...authenticate.mapGetters(['isSystemAdmin', 'currentUserGroup', 'isTenantAdmin']),
      ...sampleactivity.mapGetters(['documents']),
      ...app.mapState(['loadingHasFinish']),
      dataHeaders() {
        let headers = [{
          text: 'Date',
          value: 'SampleDateTime',
          align: 'left',
        },
        {
          text: 'Station',
          value: 'SiteId',
          align: 'left',
        },
        {
          text: 'Display Name',
          value: 'DisplayName',
          align: 'left',
        },
        {
          text: 'Envirodata Analyte Name',
          value: 'AnalyteName',
          align: 'left',
        },
        ]

        if (this.showDetails.data) {
          headers.push(...[{
            text: 'Qualifier',
            value: 'ResultQualifier',
            align: 'right',
          },
          {
            text: 'Result',
            value: 'Result',
            align: 'right',
          },
          {
            text: 'Units',
            value: 'Unit',
            align: 'left',
          },
          ])
        } else {
          headers.push(...[{
            text: 'Data',
            value: 'Units',
            align: 'right',
          }])
        }

        headers.push(...[{
          text: 'Detection Limit',
          value: 'DetectionLimit',
          align: 'right',
        },
        {
          text: 'Media Type',
          value: 'MediaType',
          align: 'left',
        },
        ])

        return headers
      },
      dataItems() {
        if (!this.currentImport) return []
        return this.currentImport.Data.map(item => ({
          ...item,
          isBelowDetectionLimit: item.Result < item.DetectionLimit,
        }))
      },
      analytesToShow() {
        if(!this.showNewOnly) {
          return this.currentImport.Analytes
        } else {
          var im = this.currentImport
          var analytes = im.NewAnalytes
          return analytes
        }
      },
      stationsToShow() {
        if(!this.showNewOnly) {
          return this.currentImport.Stations
        } else {
          var im = this.currentImport
          var stations = im.Stations.filter(x => x.Id >= im.StartStation && x.Id <= im.EndStation)
          return stations
        }
      },
    },
    methods: {
      ...imports.mapActions(['imports', 'fetchImport']),
      ...app.mapActions(['setSnackbar']),
      pluralize,
      getMediaTypeIcon,

      async getImportData() {
        try {
          let importResult = await this.fetchImport({
            importId: this.importId,
          })

          this.currentImport = importResult.ImportDetails
        } catch (error) {
          this.$refs.importDetailDialog.show(error)
        }

        try {
          await this.$store.dispatch('sampleactivity/getDocumentsByImport', this.importId)
        } catch (error) {
          this.setSnackbar({
            text: error,
            icon: 'error',
            color: 'error',
          })

          this.states.isError = true
        }

        this.states.isLoaded = true
      },
      async downloadFile(fileDetail) {
        
        var file = fileDetail

        if(file.ImportType == 'A125' && file.FileGuid != null) {
          await this.downloadViaRdr(file)
        } else {
          await this.downloadViaUrl(file)
        }
        
      },
      async downloadViaRdr(file) {
        file.loading = true

        let options = {
          successful: () => {
            file.loading = false
          },
          failedCb: (error) => {
            file.loading = false
            this.$store.dispatch('app/setSnackbar', {
              text: error,
              color: 'error',
              icon: 'error',
            })
          },
        }

        download(
          `api/RawDataRepository/file/${ file.FileGuid }/download`,
          file.FileName,
          options
        )
      },
      async downloadViaUrl(file) {
        try {
          const response = await fetch(file.FilePath)
          const blob = await response.blob()
          const objectURL = URL.createObjectURL(blob)

          const link = document.createElement('a')
          link.href = objectURL
          link.download = file.FileName || 'downloaded-file' // Fallback filename
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)

        // Cleanup: Revoke object URL after a short delay
          setTimeout(() => URL.revokeObjectURL(objectURL), 100)
        } catch (error) {
          console.error('Download failed:', error)
        }
      },
    },
    mounted() {
      this.getImportData()
    },
  }
</script>